import { ViewConfig } from '../../../models/view-config.interface';
import { ConsignorFoldersPaginatedService } from '@app/consignor/services/consignor-folders-paginated.service';
import { DeliveryNoteBundlesService } from 'api/services';
import { AppStore } from '@app/stores/appStore';
import { SelectionStore } from '@app/stores/selectionStore';
import { ConsignorViewActionsService } from '../consignor-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { lastValueFrom } from 'rxjs';
import { ColumnCreator, ColumnValue } from '../column-creator';
import {
    TableRowData,
    FolderData,
    DeliveryNoteData,
    hasStatus,
    hasReadOnly,
    hasKey,
    DeliveryNote,
    hasDeliveryNoteBundleStatus,
} from '@app/shared/models/table-data-types';

export interface ConsignorFoldersConfigDeps {
    consignorFoldersPaginatedService: ConsignorFoldersPaginatedService;
    deliveryNoteBundlesService: DeliveryNoteBundlesService;
    actionsService: ConsignorViewActionsService;
    appStore: InstanceType<typeof AppStore>;
    selectionStore: InstanceType<typeof SelectionStore>;
}

export class ConsignorFoldersConfig {
    static create(deps: ConsignorFoldersConfigDeps): ViewConfig {
        return {
            id: 'consignor-folders',
            name: 'Folders',
            translationKey: 'OUTGOING-GOODS.TOGGLER.FOLDERS',
            type: 'folders',
            userRole: 'consignor',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey = deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    return deps.consignorFoldersPaginatedService.loadConsignorFoldersPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        selectableCondition: (item: TableRowData) => {
                            const isOpen =
                                hasStatus(item) && item.status === 'open';
                            const isNotReadOnly =
                                !hasReadOnly(item) || !item.read_only;
                            return isOpen && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.details(),
                    ColumnCreator.bundleNumber(),
                    ColumnCreator.bundleName({
                        editable: true,
                        orgType: 'consignor',
                        editableCondition: (row: TableRowData) => {
                            const isOpen =
                                hasStatus(row) && row.status === 'open';
                            const isNotReadOnly =
                                !hasReadOnly(row) || !row.read_only;
                            return isOpen && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.actions({
                        width: '350px',
                        configuration: {
                            type: 'bundle',
                            showDeliveryNotesCount: true,
                            showAddNotes: true,
                            showEdit: true,
                            showWalletInvite: true,
                            showDownload: true,
                            showDelete: true,
                            showViewPdf: true,

                            // Endpoints
                            addNotesEndpoint: (item: TableRowData) =>
                                deps.actionsService.openAddNotesToBundleDialog(
                                    item as FolderData
                                ),
                            editEndpoint: (item: TableRowData) =>
                                deps.actionsService.editBundle(
                                    item as FolderData
                                ),
                            walletInviteEndpoint: (item: TableRowData) =>
                                deps.actionsService.sendWalletInvite(
                                    item as FolderData
                                ),
                            downloadEndpoint: (item: TableRowData) =>
                                deps.actionsService.downloadDeliveryBundle(
                                    item as FolderData
                                ),
                            deleteEndpoint: (item: TableRowData) =>
                                deps.actionsService.deleteFolder(
                                    item as FolderData
                                ),
                            viewPdfEndpoint: (item: TableRowData) =>
                                deps.actionsService.viewFolder(
                                    item as FolderData
                                ),

                            // Context
                            context: 'consignor',

                            // Custom visibility conditions
                            isEditVisible: (item: TableRowData) =>
                                hasStatus(item) &&
                                item.status === 'open' &&
                                !item.read_only,
                            isDeleteVisible: (item: TableRowData) =>
                                hasStatus(item) &&
                                item.status === 'open' &&
                                !item.read_only,
                            isWalletInviteVisible: (item: TableRowData) =>
                                hasStatus(item) &&
                                item.status === 'sent' &&
                                !item.read_only,
                            isAddNotesVisible: (item: TableRowData) => {
                                if (!hasStatus(item)) return false;
                                return !(
                                    item.status === 'sent' ||
                                    item.status === 'closed' ||
                                    item.read_only
                                );
                            },
                            isDownloadVisible: (item: TableRowData) => {
                                if ('numberOfDeliveryNotes' in item) {
                                    return (
                                        (item as FolderData)
                                            .numberOfDeliveryNotes > 0
                                    );
                                }
                                return false;
                            },
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.consignorName(),
                    ColumnCreator.carrierName(),
                    ColumnCreator.consigneeName(),
                    ColumnCreator.dateShipped(),
                    ColumnCreator.status({
                        header: '',
                        showDiscrepanciesIcon: true,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: TableRowData) => {
                        if ('numberOfDeliveryNotes' in item) {
                            return (
                                (item as FolderData).numberOfDeliveryNotes > 0
                            );
                        }
                        return false;
                    },
                    tableConfig: {
                        columns: [
                            ColumnCreator.actions({
                                width: '200px',
                                configuration: (
                                    row: TableRowData,
                                    value: ColumnValue,
                                    parentRow?: TableRowData
                                ) =>
                                    ({
                                        type: 'deliveryNote',
                                        showView: true,
                                        showDownload: true,
                                        showEdit: true,
                                        showDelete: true,

                                        // Endpoints
                                        viewEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.viewDeliveryNote(
                                                item as DeliveryNoteData,
                                                parentRow as FolderData
                                            ),
                                        downloadEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.downloadDeliveryNote(
                                                item as DeliveryNoteData,
                                                parentRow as FolderData
                                            ),
                                        editEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.editDeliveryNote(
                                                item as DeliveryNoteData,
                                                parentRow as FolderData
                                            ),
                                        deleteEndpoint: (
                                            item: TableRowData,
                                            bundleKey: string,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.deleteDeliveryNote(
                                                item as DeliveryNoteData,
                                                bundleKey,
                                                parentRow as FolderData
                                            ),

                                        // Configuration
                                        organizationSiteKey:
                                            parentRow?.orgSiteKey,
                                        bundleKey:
                                            parentRow && hasKey(parentRow)
                                                ? parentRow._key
                                                : undefined,
                                        bundleStatus:
                                            parentRow && hasStatus(parentRow)
                                                ? parentRow.status
                                                : undefined,
                                        context: 'consignor',

                                        // Visibility conditions
                                        isEditVisible: (item: TableRowData) => {
                                            return (
                                                parentRow &&
                                                hasStatus(parentRow) &&
                                                parentRow.status === 'open'
                                            );
                                        },
                                        isDeleteVisible: (
                                            item: TableRowData
                                        ) => {
                                            return (
                                                parentRow &&
                                                hasStatus(parentRow) &&
                                                parentRow.status === 'open'
                                            );
                                        },
                                    }) as TableActionsConfig,
                            }),
                            ColumnCreator.deliveryNoteNumber(),
                            ColumnCreator.referenceNumber({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NO',
                                orgType: 'consignor',
                                readOnly: (row, parentRow) => {
                                    const parentClosed =
                                        parentRow &&
                                        hasStatus(parentRow) &&
                                        parentRow.status === 'closed';
                                    const rowReadOnly =
                                        hasReadOnly(row) && row.read_only;
                                    const deliveryNoteClosed =
                                        hasStatus(row) &&
                                        row.status === 'closed';
                                    const bundleStatusClosed =
                                        hasDeliveryNoteBundleStatus(row) &&
                                        row.deliveryNoteBundleStatus ===
                                            'closed';
                                    return !!(
                                        parentClosed ||
                                        rowReadOnly ||
                                        deliveryNoteClosed ||
                                        bundleStatusClosed
                                    );
                                },
                            }),
                            ColumnCreator.orderNumber(),
                            ColumnCreator.dateAnnounced(),
                            ColumnCreator.originalConsignor(),
                            ColumnCreator.originalConsignorGln(),
                            ColumnCreator.status(),
                            ColumnCreator.discrepancies(),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: FolderData,
                                organizationSiteKey: string,
                                customParams?: Record<string, unknown>
                            ) => {
                                const result =
                                    deps.deliveryNoteBundlesService.getBundle({
                                        deliveryNoteBundleKey: hasKey(parentRow)
                                            ? parentRow._key
                                            : '',
                                        organizationSiteKey:
                                            parentRow.consignorKey,
                                    });
                                const bundle =
                                    result instanceof Promise
                                        ? await result
                                        : await lastValueFrom(result);
                                return (bundle.deliveryNotes || []).map(
                                    (deliveryNote: DeliveryNote) => ({
                                        ...deliveryNote,
                                        status:
                                            deliveryNote.dateReceived &&
                                            deliveryNote.dateReceived > 0
                                                ? 'closed'
                                                : bundle.status,
                                    })
                                );
                            },
                            organizationSiteKeyResolver: (row: FolderData) =>
                                row.consignorKey,
                            customParams: (row: FolderData) => ({
                                bundleKey: hasKey(row) ? row._key : undefined,
                                bundleStatus: hasStatus(row)
                                    ? row.status
                                    : undefined,
                            }),
                        },
                        loadingMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.LOADING-DELIVERY-NOTES',
                        emptyMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.NO-DELIVERY-NOTES',
                        tableClass: 'expanded-delivery-notes-table',
                        maxHeight: '400px',
                    },
                },
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'mergeDeliveryNoteBundles',
                        label: 'Merge Folders',
                        translationKey: 'OUTGOING-GOODS.BUTTONS.MERGE-FOLDERS',
                        icon: 'content_copy',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-mr-3 tw-h-14 xl:tw-px-4 xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: (item, selection) => true,
                        disabled: (() => {
                            let lastSelectionCount = 0;
                            let lastResult = true;
                            let lastSelectedKeys: string[] = [];

                            return (item, selection) => {
                                const selectedItems = deps.selectionStore.selectedItems() as FolderData[];
                                const selectionCount = selectedItems.length;

                                // Create a hash of selected keys for comparison
                                const currentSelectedKeys = selectedItems
                                    .map((bundle: FolderData) => bundle._key)
                                    .sort();
                                const keysChanged =
                                    JSON.stringify(currentSelectedKeys) !==
                                    JSON.stringify(lastSelectedKeys);

                                // Only recalculate if selection changed
                                if (
                                    selectionCount !== lastSelectionCount ||
                                    keysChanged
                                ) {
                                    lastSelectionCount = selectionCount;
                                    lastSelectedKeys = currentSelectedKeys;

                                    if (!selectedItems || selectionCount < 2) {
                                        lastResult = true;
                                        return lastResult;
                                    }

                                    const firstOrgSiteKey =
                                        selectedItems[0]?.orgSiteKey;
                                    const allSameSite = selectedItems.every(
                                        (bundle: FolderData) =>
                                            bundle.orgSiteKey ===
                                            firstOrgSiteKey
                                    );

                                    lastResult = !allSameSite;
                                }

                                return lastResult;
                            };
                        })(),
                        handler: async (item, selection) => {
                            const selectedItems = deps.selectionStore.selectedItems() as FolderData[];
                            await deps.actionsService.mergeDeliveryNoteBundles(
                                selectedItems
                            );
                        },
                    },
                    {
                        id: 'checkoutDeliveryNoteBundles',
                        label: 'Checkout Folder',
                        translationKey:
                            'OUTGOING-GOODS.BUTTONS.CHECKOUT-FOLDER',
                        svgIcon: 'folder-checkout',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-mx-3 tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        disabled: (() => {
                            let lastSelectionCount = 0;
                            let lastResult = true;
                            let lastSelectedKeys: string[] = [];

                            return (item, selection) => {
                                const selectedItems = deps.selectionStore.selectedItems() as FolderData[];
                                const selectionCount = deps.selectionStore.selectionCount();

                                // Create a hash of selected keys for comparison
                                const currentSelectedKeys = selectedItems
                                    .map((bundle: FolderData) => bundle._key)
                                    .sort();
                                const keysChanged =
                                    JSON.stringify(currentSelectedKeys) !==
                                    JSON.stringify(lastSelectedKeys);

                                // Only recalculate if selection changed
                                if (
                                    selectionCount !== lastSelectionCount ||
                                    keysChanged
                                ) {
                                    lastSelectionCount = selectionCount;
                                    lastSelectedKeys = currentSelectedKeys;

                                    if (
                                        !selectedItems ||
                                        selectionCount === 0
                                    ) {
                                        lastResult = true;
                                        return lastResult;
                                    }

                                    // Check if all selected bundles are valid for checkout
                                    const allBundlesValid = selectedItems.every(
                                        (bundle: FolderData) => {
                                            return bundle.status === 'open';
                                        }
                                    );

                                    lastResult = !allBundlesValid;
                                }

                                return lastResult;
                            };
                        })(),
                        handler: async (item, selection) => {
                            const selectedItems = deps.selectionStore.selectedItems() as FolderData[];
                            await deps.actionsService.checkoutDeliveryNoteBundles(
                                selectedItems
                            );
                        },
                    },
                    {
                        id: 'createNewDeliveryNoteBundle',
                        label: 'Create New Folder',
                        translationKey:
                            'OUTGOING-GOODS.BUTTONS.CREATE-NEW-FOLDER',
                        svgIcon: 'folder-create',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 3,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => false,
                        handler: async (item, selection) => {
                            await deps.actionsService.createNewDeliveryNoteBundle();
                        },
                    },
                ],
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'bundleNumber',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                    },
                    {
                        keyName: 'name',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME',
                    },
                    {
                        keyName: 'consignorName',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CARRIER-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CARRIER-NAME',
                    },
                    {
                        keyName: 'consigneeName',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
                    },
                    {
                        keyName: 'checkoutDate',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                    },
                ],
                showClosed: false,
                enableAdvancedFilters: false,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'name',
                    'bundleNumber',
                    'status',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'checkoutDate',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: true,
                canUpdate: true,
                canDelete: true,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
